import { createContext, useContext } from 'react';
import { z } from 'zod';

export const THEMES = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'sepia', label: 'Sepia' },
  { value: 'dark', label: 'Dark' },
  { value: 'navy', label: 'Navy' },
] as const;

export const themeSchema = z.enum(['system', 'light', 'sepia', 'dark', 'navy']);
export type ThemeChoice = z.infer<typeof themeSchema>;
export type AppliedTheme = Exclude<ThemeChoice, 'system'>;

/** The ground colour of each theme, for the browser and Android status bars. */
export const THEME_GROUND: Record<AppliedTheme, string> = {
  light: '#f6f5f2',
  sepia: '#f2ead9',
  dark: '#111317',
  navy: '#1a2039',
};
export const DARK_THEMES: ReadonlySet<AppliedTheme> = new Set(['dark', 'navy']);

export const THEME_KEY = 'libellus.theme';

export interface ThemeContextValue {
  choice: ThemeChoice;
  applied: AppliedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function systemTheme(): AppliedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
